import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  ListGroup,
  ListGroupItem,
  Badge,
  Col,
  Button,
} from "reactstrap";
import { ShoppingCart, CheckCircle, BarChart, Server, CreditCard, AlertCircle, ThumbsUp, ThumbsDown } from "react-feather";

const NotificationsPanel2 = ({ data = [], title = "Notifications" }) => {
  const [openAccordion, setOpenAccordion] = useState(null);
  const [visibleItems, setVisibleItems] = useState(3); // Initial visible items per section

  // Group notifications by type and map to UI sections
  const groupedNotifications = data.reduce((acc, notification) => {
    const type = notification.type;

    if (!acc[type]) {
      // Initialize section if it doesn't exist
      acc[type] = {
        icon: getIconForType(type),
        color: getColorForType(type),
        category: getCategoryName(type),
        items: []
      };
    }

    acc[type].items.push({
      id: notification._id,
      text: notification.title || notification.body || 'New notification',
      time: formatTime(notification.createdAt),
      unread: !notification.isRead
    });

    return acc;
  }, {});

  // Convert to array and sort by most recent notification in each group
  const notificationSections = Object.values(groupedNotifications)
    .map(section => ({
      ...section,
      items: section.items.sort((a, b) => new Date(b.time) - new Date(a.time))
    }))
    .sort((a, b) => {
      // Sort sections by most recent item
      const latestA = a.items[0]?.time || 0;
      const latestB = b.items[0]?.time || 0;
      return new Date(latestB) - new Date(latestA);
    });

  // Helper functions
  function getIconForType(type) {
    switch (type) {
      case 'new_order': return 'shopping-cart';
      case 'subscription': return 'check-circle';
      case 'class': return 'bar-chart';
      case 'payout_approved': return 'credit-card';
      case 'payout_rejected': return 'alert-circle';
      case 'banner_approved': return 'thumbs-up';
      case 'banner_rejected': return 'thumbs-down';
      default: return 'server';
    }
  }

  function getColorForType(type) {
    switch (type) {
      case 'new_order': return 'primary';
      case 'subscription': return 'success';
      case 'class': return 'warning';
      case 'payout_approved': return 'info';
      case 'payout_rejected': return 'danger';
      case 'banner_approved': return 'success';
      case 'banner_rejected': return 'danger';
      default: return 'secondary';
    }
  }

  function getCategoryName(type) {
    switch (type) {
      case 'new_order': return 'New Orders';
      case 'subscription': return 'Subscriptions';
      case 'class': return 'Class Updates';
      case 'payout_approved': return 'Payouts Approved';
      case 'payout_rejected': return 'Payouts Rejected';
      case 'banner_approved': return 'Banners Approved';
      case 'banner_rejected': return 'Banners Rejected';
      default: return 'Other Notifications';
    }
  }

  function formatTime(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString(); // Adjust format as needed
  }

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const loadMore = (sectionIndex) => {
    // For simplicity, we'll just increase all sections
    setVisibleItems(prev => prev + 3);
  };

  // Icon mapping
  const iconComponents = {
    'shopping-cart': ShoppingCart,
    'check-circle': CheckCircle,
    'bar-chart': BarChart,
    'credit-card': CreditCard,
    'alert-circle': AlertCircle,
    'thumbs-up': ThumbsUp,
    'thumbs-down': ThumbsDown,
    'server': Server
  };

  return (
    <Col xl="12" className="box-col-12">
      <Card className="notification-panel">
        <CardHeader className="card-no-border">
          <h5 className="text-dark">{title}</h5>
        </CardHeader>
        <CardBody className="pt-0">
          {data.length === 0 ? <div className="text-center py-4">
            <div className="empty-notification-icon mb-3">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
            <h5 className="text-muted mb-2">No Notifications Yet</h5>
            <p className="text-muted small">
              You'll see important updates here when they arrive
            </p>
          </div> : <Accordion open={openAccordion} toggle={toggleAccordion}>
            {notificationSections.slice(0, 4).map((section, index) => {
              const IconComponent = iconComponents[section.icon] || Server;
              return (
                <AccordionItem key={index} className="mb-2 border rounded">
                  <AccordionHeader targetId={`${index + 1}`}>
                    <div className="d-flex align-items-center w-100">
                      <span className={`bg-${section.color} p-2 rounded-circle d-flex align-items-center justify-content-center`}>
                        <IconComponent size={18} className="text-white" />
                      </span>
                      <span className="flex-grow-1 text-dark ms-2 fw-bold">{section.category}</span>
                      {/* <Badge color={section.color} pill>
                        {section.items.filter(item => item.unread).length}
                      </Badge> */}
                    </div>
                  </AccordionHeader>

                  <AccordionBody accordionId={`${index + 1}`} className="bg-light">
                    <ListGroup flush>
                      {section.items.slice(0, visibleItems).map((item, itemIndex) => (
                        <ListGroupItem
                          key={itemIndex}
                          className={`d-flex align-items-center border-0 rounded p-3 mt-2 ${item.unread ? "bg-light-primary" : "bg-white"}`}
                          style={{
                            transition: "all 0.3s ease",
                            cursor: "pointer",
                          }}
                        >
                          <div className="flex-grow-1">
                            <span className="d-block fw-bold text-dark">{item.text}</span>
                            <small className="text-muted">{item.time}</small>
                          </div>
                          {item.unread && (
                            <Badge color="danger" className="ms-2">
                              New
                            </Badge>
                          )}
                        </ListGroupItem>
                      ))}
                    </ListGroup>
                    {visibleItems < section.items.length && (
                      <div className="text-center mt-2">
                        <Button color="primary" size="sm" onClick={() => loadMore(index)}>
                          Load More
                        </Button>
                      </div>
                    )}
                  </AccordionBody>
                </AccordionItem>
              );
            })}
          </Accordion>}
        </CardBody>
      </Card>
    </Col>
  );
};

export default NotificationsPanel2;